import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  ButtonGroup,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography
} from "@mui/material";

import {
  BCPAngleDown,
  BCPAngleRight,
  BCPAnglesDown,
  BCPAnglesUp,
  BCPEye,
  BCPMinus,
  BCPPlus
} from "../../../components/icons/bcp/BCPIcons";

import { Formik } from "formik";
import { AuthContext } from "../../../components/user/BCPAuthProvider";
import { postNotification } from "../../../components/utilities/Notifications";
import {
  handlePaging,
  handlePagingAll
} from "../../../components/api/APIUtilities";
import TextFieldSelect from "../../../components/utilities/TextFieldSelect";
import ListResponsive from "../../../components/other/ListResponsive";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";
import StyledMenu from "../../../components/layout/StyledMenu";
import CardCard from "./CardCard";
import HelpButton from "../../../components/utilities/HelpButton";
import { getEnvironmentVariable } from "../../../global/environmentVariables";

const SINGLE_GAMETYPE = getEnvironmentVariable("SINGLE_GAMETYPE");
const SINGLE_GAME_SYSTEM = getEnvironmentVariable("SINGLE_GAME_SYSTEM");

export default function CardSearch(props) {
  const {
    gameSystem,
    cardUtil,
    heading,
    containerSX,
    cardLeft,
    cardRight,
    cardView,
    buttons,
    dense,
    resultsHelperText,
    deckBuilder
  } = props;

  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  const [loadingPage, setLoadingPage] = useState(true);
  const [armies, setArmies] = useState(props.armies);

  const [isSearching, setIsSearching] = useState(null);
  const [advancedSearch, setAdvancedSearch] = useState(false);

  const [searchValues, setSearchValues] = useState(cardUtil?.blankSearch || {});
  const [sortAnchor, setSortAnchor] = useState(null);

  const [cards, setCards] = useState(null);
  const [nextKey, setNextKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filtersState, setFiltersState] = useState(null);

  const fetchCards = async () => {
    if (!filtersState) {
      postNotification("error", "No filters selected.");
      return;
    }

    try {
      var apiCards = await handlePaging(
        "/cards",
        filtersState,
        nextKey,
        setNextKey
      );

      apiCards = apiCards?.filter(
        (el) => !deckBuilder || el.cardType !== "Cosmic Avatar"
      );

      setCards(cards ? [...cards, ...apiCards] : apiCards);

      if (apiCards.length !== filtersState.limit) {
        setHasMore(false);
        if (!apiCards?.length) {
          postNotification(
            "info",
            "No cards found matching your search criteria."
          );
          setIsSearching(false);
          return;
        }
      }
    } catch (err) {
      postNotification("error", err);
    }

    setIsSearching(false);
  };

  useEffect(() => {
    if (filtersState) {
      fetchCards();
    }
    // eslint-disable-next-line
  }, [filtersState]);

  useEffect(() => {
    const loadPage = async () => {
      setLoadingPage(true);

      if (!armies) {
        if (!gameSystem?.code && SINGLE_GAMETYPE === undefined) {
          postNotification(
            `No ${getEnvironmentVariable(
              "LABEL_GAMESYSTEM"
            )}s setup with a card database`
          );
          navigate("/");
          return;
        }
        var apiArmies = await handlePagingAll(`/armies`, {
          gameType: gameSystem?.code || SINGLE_GAMETYPE
        });
        setArmies(apiArmies);
      }

      setLoadingPage(false);
    };
    loadPage();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {!loadingPage && (
        <>
          {/* {!dense && authContext?.authState?.identity?.isAdmin === true && (
            <CardUploadTool cardUtil={cardUtil} armies={armies} />
          )} */}
          <Grid container sx={{ gap: 2, ...containerSX }}>
            {heading && (
              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                  borderRadius: 1,
                  boxShadow: 1,
                  backgroundColor: "background.secondary"
                }}
              >
                <Typography variant="h3">{heading}</Typography>
              </Grid>
            )}
            <Grid
              item
              xs={12}
              sx={{
                p: 2,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: 1,
                boxShadow: 1,
                backgroundColor: "background.secondary"
              }}
            >
              <Formik
                initialValues={searchValues}
                enableReinitialize
                onSubmit={async (values) => {
                  try {
                    setIsSearching(true);
                    setNextKey(null);
                    setHasMore(true);
                    setCards([]);

                    var filters = {
                      limit: 100,
                      sortKey: values.sortKey || undefined,
                      sortAscending:
                        values.sortKey !== "_score"
                          ? values.sortAscending
                          : true,
                      "expand[]": ["imageFile"],
                      gameSystemId: gameSystem?.id || SINGLE_GAME_SYSTEM
                    };

                    if (values.highlightSearch) {
                      filters.highlightSearch = true;
                    }
                    if (values.showPublicOnly) {
                      filters.public = true;
                    }
                    if (values.includeVariants) {
                      filters.includeVariants = true;
                    }
                    if (values.searchString !== "") {
                      filters.searchString = values.searchString;
                    }
                    if (values.nameSearch !== "") {
                      filters.nameSearch = values.nameSearch;
                    }
                    if (values.textSearch !== "") {
                      filters.textSearch = values.textSearch;
                    }

                    if (values.serials?.length) {
                      filters["serials[]"] = values.serials;
                    }
                    if (values.rarities?.length) {
                      filters["rarities[]"] = values.rarities;
                    }

                    if (values.cardTypes?.length) {
                      filters["cardTypes[]"] = values.cardTypes;
                    }
                    if (values.cardSubTypes?.length) {
                      filters["cardSubTypes[]"] = values.cardSubTypes;
                    }
                    if (values.cardSubTypeChildren?.length) {
                      filters["cardSubTypeChildren[]"] =
                        values.cardSubTypeChildren;
                    }
                    if (values.resists?.length) {
                      filters["resists[]"] = values.resists;
                    }
                    if (values.traits?.length) {
                      filters["traits[]"] = values.traits;
                    }

                    if (values.keywords?.length) {
                      filters["keywords[]"] = values.keywords;
                      if (values.keywordsAll) {
                        filters["keywords-All"] = true;
                      }
                    }

                    if (values.factions?.length) {
                      filters["factions[]"] = values.factions;
                    }

                    setSearchValues(values);
                    setFiltersState(filters);
                  } catch (error) {
                    postNotification("error", error);
                    setIsSearching(false);
                  }
                }}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  touched,
                  values,
                  setFieldValue,
                  setFieldTouched,
                  handleReset
                }) => (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          name="nameSearch"
                          label="Name Search"
                          value={values.nameSearch}
                          variant="outlined"
                          fullWidth
                          margin="none"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.nameSearch && errors.nameSearch
                          )}
                          helperText={touched.nameSearch && errors.nameSearch}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="searchString"
                          label="Dynamic Search"
                          value={values.searchString}
                          variant="outlined"
                          fullWidth
                          margin="none"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.searchString && errors.searchString
                          )}
                          helperText={
                            touched.searchString && errors.searchString
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <HelpButton
                                  title={`Each word will be used as a seperate search term, using "" around a group of words will combine it as a single search term. The search will then check the following fields`}
                                />
                              </InputAdornment>
                            )
                          }}
                        />
                        {cardUtil.dynamicSearch?.helperText && (
                          <FormHelperText>
                            {cardUtil.dynamicSearch?.helperText}
                          </FormHelperText>
                        )}
                      </Grid>

                      {authContext?.authState?.identity?.isAdmin === true && (
                        <Grid item xs={12}>
                          <FormControlLabel
                            label={
                              <Typography className="MuiFormControlLabel-label">
                                Show public only
                              </Typography>
                            }
                            sx={{
                              ml: 0,
                              width: "100%",
                              "& .MuiFormControlLabel-label": {
                                width: "calc(100% - 58px)"
                              }
                            }}
                            control={
                              <Switch
                                color="primary"
                                checked={values.showPublicOnly}
                                onChange={(e) => {
                                  setFieldValue(
                                    "showPublicOnly",
                                    !values.showPublicOnly
                                  );
                                }}
                              />
                            }
                            labelPlacement="start"
                          />
                        </Grid>
                      )}

                      {!deckBuilder ? (
                        <Grid item xs={12}>
                          <FormControlLabel
                            label={
                              <Typography className="MuiFormControlLabel-label">
                                Include Card Variants
                              </Typography>
                            }
                            sx={{
                              ml: 0,
                              width: "100%",
                              "& .MuiFormControlLabel-label": {
                                width: "calc(100% - 58px)",
                              },
                            }}
                            control={
                              <Switch
                                color="primary"
                                checked={values.includeVariants}
                                onChange={(e) => {
                                  setFieldValue(
                                    "includeVariants",
                                    !values.includeVariants
                                  );
                                }}
                              />
                            }
                            labelPlacement="start"
                          />
                        </Grid>
                      ) : (
                        <></>
                      )}

                      <Grid
                        item
                        xs={12}
                        onClick={() => {
                          setAdvancedSearch(!advancedSearch);
                        }}
                        sx={{
                          "&:hover": {
                            cursor: "pointer"
                          }
                        }}
                      >
                        <Typography
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap"
                          }}
                        >
                          {advancedSearch ? (
                            <BCPAngleDown />
                          ) : (
                            <BCPAngleRight />
                          )}
                          <span>Advanced Search</span>
                        </Typography>
                      </Grid>
                      {advancedSearch && (
                        <>
                          <Grid item xs={12}>
                            <TextField
                              name="textSearch"
                              label="Text Search"
                              value={values.textSearch}
                              variant="outlined"
                              fullWidth
                              margin="none"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.textSearch && errors.textSearch
                              )}
                              helperText={
                                touched.textSearch && errors.textSearch
                              }
                            />
                          </Grid>
                          {cardUtil.cardSets?.length > 0 && (
                            <Grid item xs={12}>
                              <TextFieldSelect
                                list={cardUtil.cardSets.map((cardSet) => {
                                  return {
                                    value: cardSet.serial,
                                    label: cardSet.name
                                  };
                                })}
                                name="serials"
                                label="Card Set"
                                value={values.serials}
                                fullWidth
                                variant="outlined"
                                margin="none"
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                error={Boolean(
                                  touched.serials && errors.serials
                                )}
                                helperText={touched.serials && errors.serials}
                                multiple
                                clearAll
                                renderValue={(selected) => {
                                  return selected.join(", ");
                                }}
                              />
                            </Grid>
                          )}
                          {cardUtil.cardTypes?.length > 0 && (
                            <Grid item xs={12}>
                              <TextFieldSelect
                                list={
                                  cardUtil.cardTypes
                                    ?.map((el) => el?.name)
                                    ?.filter(
                                      (el) =>
                                        !deckBuilder || el !== "Cosmic Avatar"
                                    ) || []
                                }
                                name="cardTypes"
                                label="Card Type"
                                value={values.cardTypes}
                                fullWidth
                                variant="outlined"
                                margin="none"
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                error={Boolean(
                                  touched.cardTypes && errors.cardTypes
                                )}
                                helperText={
                                  touched.cardTypes && errors.cardTypes
                                }
                                multiple
                                clearAll
                              />
                            </Grid>
                          )}
                          {values.cardTypes?.length === 1 &&
                            cardUtil.cardSubTypes?.[values.cardTypes[0]]
                              ?.cardSubTypes?.length > 0 && (
                              <Grid item xs={12}>
                                <TextFieldSelect
                                  list={
                                    cardUtil.cardSubTypes?.[values.cardTypes[0]]
                                      ?.cardSubTypes
                                  }
                                  name="cardSubTypes"
                                  label={
                                    cardUtil.cardSubTypes?.[values.cardTypes[0]]
                                      ?.cardSubTypeLabel
                                  }
                                  value={values.cardSubTypes}
                                  fullWidth
                                  variant="outlined"
                                  margin="none"
                                  setFieldValue={setFieldValue}
                                  setFieldTouched={setFieldTouched}
                                  error={Boolean(
                                    touched.cardSubTypes && errors.cardSubTypes
                                  )}
                                  helperText={
                                    touched.cardSubTypes && errors.cardSubTypes
                                  }
                                  multiple
                                  clearAll
                                />
                              </Grid>
                            )}

                          {values.cardTypes?.length === 1 &&
                            cardUtil.cardSubTypes?.[values.cardTypes[0]]
                              ?.cardSubTypeChildren?.length > 0 && (
                              <Grid item xs={12}>
                                <TextFieldSelect
                                  list={
                                    cardUtil.cardSubTypes?.[values.cardTypes[0]]
                                      ?.cardSubTypeChildren
                                  }
                                  name="cardSubTypeChildren"
                                  label={
                                    cardUtil.cardSubTypes?.[values.cardTypes[0]]
                                      ?.cardSubTypeChildLabel
                                  }
                                  value={values.cardSubTypeChildren}
                                  fullWidth
                                  variant="outlined"
                                  margin="none"
                                  setFieldValue={setFieldValue}
                                  setFieldTouched={setFieldTouched}
                                  error={Boolean(
                                    touched.cardSubTypeChildren &&
                                      errors.cardSubTypeChildren
                                  )}
                                  helperText={
                                    touched.cardSubTypeChildren &&
                                    errors.cardSubTypeChildren
                                  }
                                  multiple
                                  clearAll
                                />
                              </Grid>
                            )}

                          {cardUtil.traits?.length > 0 && (
                            <Grid item xs={12}>
                              <TextFieldSelect
                                list={cardUtil.traits}
                                name="traits"
                                label="Traits"
                                value={values.traits}
                                fullWidth
                                variant="outlined"
                                margin="none"
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                error={Boolean(touched.traits && errors.traits)}
                                helperText={touched.traits && errors.traits}
                                multiple
                                clearAll
                              />
                            </Grid>
                          )}
                          {cardUtil.resists?.length > 0 && (
                            <Grid item xs={12}>
                              <TextFieldSelect
                                list={cardUtil.resists}
                                name="resists"
                                label="Resists"
                                value={values.resists}
                                fullWidth
                                variant="outlined"
                                margin="none"
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                error={Boolean(
                                  touched.resists && errors.resists
                                )}
                                helperText={touched.resists && errors.resists}
                                multiple
                                clearAll
                              />
                            </Grid>
                          )}

                          {cardUtil.rarities?.length > 0 && (
                            <Grid item xs={12}>
                              <TextFieldSelect
                                list={cardUtil.rarities.map((rarity) => {
                                  return rarity.name;
                                })}
                                name="rarities"
                                label="Rarity"
                                value={values.rarities}
                                fullWidth
                                variant="outlined"
                                margin="none"
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                error={Boolean(
                                  touched.rarities && errors.rarities
                                )}
                                helperText={touched.rarities && errors.rarities}
                                multiple
                                clearAll
                              />
                            </Grid>
                          )}

                          {cardUtil.keywords?.length > 0 && (
                            <Grid item xs={12}>
                              <TextFieldSelect
                                list={cardUtil.keywords}
                                name="keywords"
                                label="Keywords"
                                value={values.keywords}
                                fullWidth
                                variant="outlined"
                                margin="none"
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                error={Boolean(
                                  touched.keywords && errors.keywords
                                )}
                                helperText={touched.keywords && errors.keywords}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Button
                                        sx={{ minWidth: "40px" }}
                                        onClick={() => {
                                          setFieldValue(
                                            "keywordsAll",
                                            !values.keywordsAll
                                          );
                                        }}
                                      >
                                        {values.keywordsAll ? "and" : "or"}
                                      </Button>
                                    </InputAdornment>
                                  )
                                }}
                                multiple
                                clearAll
                              />
                              <FormHelperText>
                                Select OR to search for cards with any of the
                                keywords. Select AND to search for cards with
                                all of the keywords.
                              </FormHelperText>
                            </Grid>
                          )}

                          {cardUtil.masterKeys?.filter(
                            (el) => el.key === "factions"
                          )?.length > 0 && (
                            <Grid item xs={12}>
                              <TextFieldSelect
                                list={armies.map((army) => {
                                  return army.name;
                                })}
                                name="factions"
                                label={gameSystem?.factionLabel || "Factions"}
                                value={values.factions}
                                fullWidth
                                variant="outlined"
                                margin="none"
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                error={Boolean(
                                  touched.factions && errors.factions
                                )}
                                helperText={touched.factions && errors.factions}
                                multiple
                                clearAll
                              />
                            </Grid>
                          )}

                          {/* {!dense && (
                            <Grid item xs={12}>
                              <FormControlLabel
                                label={
                                  <Typography className="MuiFormControlLabel-label">
                                    Highlight Search Results
                                  </Typography>
                                }
                                sx={{
                                  ml: 0,
                                  width: "100%",
                                  "& .MuiFormControlLabel-label": {
                                    width: "calc(100% - 58px)",
                                  },
                                }}
                                control={
                                  <Switch
                                    color="primary"
                                    checked={values.highlightSearch}
                                    onChange={(e) => {
                                      setFieldValue(
                                        "highlightSearch",
                                        !values.highlightSearch
                                      );
                                    }}
                                  />
                                }
                                labelPlacement="start"
                              />
                            </Grid>
                          )} */}
                        </>
                      )}

                      <Grid item xs={12} md={4}>
                        <ButtonGroup variant="outlined" fullWidth>
                          {values.sortKey !== "_score" && (
                            <Button
                              sx={{ maxWidth: "60px" }}
                              onClick={() => {
                                setFieldValue(
                                  "sortAscending",
                                  !values.sortAscending
                                );
                              }}
                            >
                              {values.sortAscending ? (
                                <BCPAnglesUp />
                              ) : (
                                <BCPAnglesDown />
                              )}
                            </Button>
                          )}
                          <Button
                            onClick={(e) => {
                              setSortAnchor(e.currentTarget);
                            }}
                          >
                            Sort by {cardUtil?.sortOptions[values.sortKey]}
                          </Button>
                        </ButtonGroup>
                        <SortMenu
                          sortOptions={cardUtil?.sortOptions}
                          setFieldValue={setFieldValue}
                          sortKey={values.sortKey}
                          anchorEl={sortAnchor}
                          setAnchorEl={setSortAnchor}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="error"
                          onClick={(e) => {
                            setSearchValues(cardUtil?.blankSearch || {});
                            setAdvancedSearch(false);
                            setCards([]);
                            handleReset(e);
                          }}
                        >
                          Reset
                        </Button>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="success"
                          type="submit"
                        >
                          Search
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                )}
              </Formik>
            </Grid>

            <CardList
              cards={cards}
              cardLeft={cardLeft}
              cardRight={cardRight}
              cardView={cardView}
              buttons={buttons}
              resultsHelperText={resultsHelperText}
              fetchCards={fetchCards}
              hasMore={hasMore}
              dense={dense}
              deckBuilder={deckBuilder}
            />
          </Grid>
        </>
      )}
      {(loadingPage || isSearching) && <ModalLoadingFull />}
    </>
  );
}

function CardList(props) {
  const {
    cards,
    cardLeft,
    cardRight,
    cardView,
    buttons,
    fetchCards,
    hasMore,
    dense
  } = props;

  if (!cards?.length) return <></>;

  if (dense) {
    return (
      <Grid
        item
        xs={12}
        sx={{
          p: 2,
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          boxShadow: 1,
          backgroundColor: "background.secondary"
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "370px",
            overflow: "auto",
            contain: "strict",
            overflowY: "hidden"
          }}
        >
          {cards.map((card, index) => (
            <CardCard
              key={card.id}
              card={card}
              leftClick={buttons && cardView ? cardView : cardLeft}
              rightClick={buttons ? null : cardRight}
              dense={dense}
              buttons={
                buttons ? (
                  <Box>
                    <ButtonGroup fullWidth>
                      <Button
                        color="primary"
                        variant="outlined"
                        onClick={
                          cardLeft
                            ? (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                cardLeft(card);
                              }
                            : null
                        }
                      >
                        <BCPMinus />
                      </Button>
                      <Button
                        color="primary"
                        variant="outlined"
                        onClick={
                          cardView
                            ? (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                cardView(card);
                              }
                            : null
                        }
                      >
                        <BCPEye />
                      </Button>
                      <Button
                        color="primary"
                        variant="outlined"
                        onClick={
                          cardRight
                            ? (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                cardRight(card);
                              }
                            : null
                        }
                      >
                        <BCPPlus />
                      </Button>
                    </ButtonGroup>
                  </Box>
                ) : null
              }
            />
          ))}
        </Box>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <ListResponsive
        originalList={cards}
        loadMore={fetchCards}
        hasMore={hasMore}
        scrollToTop
        sx={{
          flexFlow: "row wrap",
          boxSizing: "border-box",
          display: "flex",
          placeContent: "center space-between",
          alignItems: "center"
        }}
        rowProps={{
          mapFunction: (row, index) => (
            <CardCard
              key={index}
              card={row}
              leftClick={cardLeft}
              rightClick={cardRight}
              dense={dense}
            />
          )
        }}
      />
    </Grid>
  );
}

function SortMenu(props) {
  const { sortOptions, setFieldValue, sortKey, anchorEl, setAnchorEl } = props;
  const open = Boolean(anchorEl);

  return (
    <StyledMenu
      id="sort-options-menu"
      disableScrollLock
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
    >
      {Object.keys(sortOptions).map((sortOption) => {
        return (
          <MenuItem
            key={sortOption}
            selected={sortOption === sortKey}
            onClick={(e) => {
              setFieldValue("sortKey", sortOption);
              setAnchorEl(null);
            }}
            disableRipple
          >
            Sort by {sortOptions[sortOption]}
          </MenuItem>
        );
      })}
    </StyledMenu>
  );
}
